import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import type { DialerDispositionBody } from '@brimo/shared';
import { Banner, Skeleton } from '../../../ds/components';
import { ApiError, errorMessage } from '../../../lib/api';
import { useTelephonyStatus } from '../../telephony/api';
import { elapsedSeconds, isLive } from '../../telephony/callState';
import { LiveTranscriptPanel } from '../../telephony/LiveTranscriptPanel';
import { useCall, useClock } from '../../telephony/useCall';
import {
  SUMMARY_LOADING_TEXT,
  SUMMARY_SUGGESTION_TEXT,
  useCallSummary,
} from '../../telephony/useCallSummary';
import {
  bookFromSession,
  dialerKeys,
  endSession,
  saveDisposition,
  sessionAction,
  useDialerState,
  useEvaluation,
  useTodayMetrics,
  type DialerState,
  type EventTypeRef,
  type Glow,
} from '../api';
import { Crumb, DialerPage, LoadError, MetricsBar } from '../ui';
import { BookingCard } from './BookingCard';
import { BriefingCard, ContactColumn, NoteCard, TimelineCard, liveStatus, type LiveRow } from './CallView';
import { DialerQueueBar, type BarPhase } from './DialerQueueBar';
import {
  applySummary,
  DispositionView,
  useDispositionDefault,
  type DispositionDraft,
} from './DispositionView';
import { GlowOverlay } from './GlowOverlay';
import { PauseView } from './PauseView';
import styles from './session.module.css';

const EMPTY_DRAFT: DispositionDraft = { disposition: null, note: '', followUpOn: '', followUpTime: '' };

/** „Termin jetzt buchen" of the live hints: brings the booking card into view and focuses its first control. */
function focusBooking(): void {
  const card = document.getElementById('dialer-booking-title')?.closest('section');
  if (!card) return;
  card.scrollIntoView({ block: 'center' });
  card.querySelector<HTMLElement>('button:not(:disabled), input:not(:disabled)')?.focus();
}

interface Saved {
  entryId: string;
  name: string;
  durationSeconds: number | null;
  booked: boolean;
  recordingSaved: boolean;
  draft: DispositionDraft;
  suggestion: string | null;
}

/**
 * Terminierung 08–11 — the running dialer session: navy queue bar and metrics over the contact record of the
 * current contact (08; 08b when the event type has to be chosen), the booking with the glow-up (09), the
 * mandatory result after the call (10) with the power countdown, and the pause with the session evaluation
 * (11). Power mode dials the current contact right away; Preview waits for „Anrufen".
 */
export function DialerSessionPage() {
  const params = useParams();
  const sessionId = params['id'] ?? '';
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const dialer = useDialerState();
  const metrics = useTodayMetrics();
  const telephony = useTelephonyStatus();
  const call = useCall();
  const state = dialer.data;
  const session = state?.session ?? null;
  const current = state?.current ?? null;
  const entryId = current?.entryId ?? null;

  const [notes, setNotes] = useState<Record<string, string>>({});
  const [drafts, setDrafts] = useState<Record<string, DispositionDraft>>({});
  const [chosenTypes, setChosenTypes] = useState<Record<string, EventTypeRef>>({});
  const [booked, setBooked] = useState<Record<string, Glow>>({});
  const [glow, setGlow] = useState<Glow | null>(null);
  const [booking, setBooking] = useState<string | null>(null);
  const [bookingError, setBookingError] = useState<string | null>(null);
  const [saved, setSaved] = useState<Saved | null>(null);
  const [countdown, setCountdown] = useState<number | null>(null);
  const [pending, setPending] = useState<'save' | 'next' | 'pause' | 'resume' | 'end' | null>(null);
  const [banner, setBanner] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<{
    reason?: string | undefined;
    followUp?: string | undefined;
  }>({});
  const autoDialed = useRef<string | null>(null);

  const liveHere =
    current !== null &&
    call.state.source === 'dialer' &&
    call.state.contactId === current.contact.id &&
    isLive(call.state.phase);
  const endedHere =
    current !== null &&
    call.state.source === 'dialer' &&
    call.state.contactId === current.contact.id &&
    (call.state.phase === 'ended' || call.state.phase === 'failed');
  const now = useClock(liveHere);
  const localSeconds = liveHere || endedHere ? elapsedSeconds(call.state, now) : null;

  const setState = useCallback(
    (next: DialerState) => queryClient.setQueryData(dialerKeys.current(), next),
    [queryClient],
  );

  // --- view ------------------------------------------------------------------------------------------
  type View = 'loading' | 'other' | 'pause' | 'saved' | 'between' | 'exhausted' | 'call' | 'disposition';
  let view: View;
  if (!state) view = 'loading';
  else if (session === null || session.id !== sessionId) view = 'other';
  else if (session.status === 'paused') view = 'pause';
  else if (saved !== null) view = 'saved';
  else if (current === null) view = state.nextUp ? 'between' : 'exhausted';
  else if (liveHere) view = 'call';
  else if (current.awaitingDisposition || endedHere) view = 'disposition';
  else view = 'call';

  // --- power: dial the current contact right away ------------------------------------------------------
  useEffect(() => {
    if (view !== 'call' || session?.dialMode !== 'power' || current === null || session.status !== 'running')
      return;
    if (current.call !== null || liveHere || autoDialed.current === current.entryId) return;
    autoDialed.current = current.entryId;
    call.reset();
    void call.start({ contactId: current.contact.id, source: 'dialer', sessionId: session.id });
  }, [view, session, current, liveHere, call]);

  // --- countdown after a saved result (power) ---------------------------------------------------------
  const next = useCallback(async () => {
    if (session === null) return;
    setPending('next');
    setBanner(null);
    try {
      const fresh = await sessionAction(session.id, 'next');
      setState(fresh);
      setSaved(null);
      setCountdown(null);
    } catch (caught) {
      setBanner(errorMessage(caught));
      setCountdown(null);
    } finally {
      setPending(null);
    }
  }, [session, setState]);

  useEffect(() => {
    if (countdown === null) return;
    if (countdown <= 0) {
      void next();
      return;
    }
    const id = window.setTimeout(() => setCountdown((value) => (value === null ? null : value - 1)), 1000);
    return () => window.clearTimeout(id);
  }, [countdown, next]);

  // --- disposition draft --------------------------------------------------------------------------------
  const draftKey = entryId;
  const draft = (draftKey ? drafts[draftKey] : undefined) ?? {
    ...EMPTY_DRAFT,
    note: (entryId ? notes[entryId] : undefined) ?? '',
  };
  const setDraft = useCallback(
    (value: DispositionDraft) => {
      if (draftKey) setDrafts((all) => ({ ...all, [draftKey]: value }));
    },
    [draftKey],
  );
  const bookedHere = entryId !== null && booked[entryId] !== undefined;
  const serverDuration = current?.call?.durationSeconds ?? null;
  const reached =
    localSeconds !== null || serverDuration !== null || (current?.call?.connectedAt ?? null) !== null;
  useDispositionDefault(view === 'disposition' ? entryId : null, bookedHere, reached, draft, setDraft);

  // KI-Zusammenfassung: a connected call's result dialog asks for the summary once and prefills note, result and
  // follow-up as a suggestion (never saved by itself); without a summary the dialog stays as it is.
  const summaryCallId =
    view === 'disposition' && reached ? (current?.call?.id ?? (endedHere ? call.state.callId : null)) : null;
  const aiSummary = useCallSummary(summaryCallId);
  const summaryApplied = useRef<string | null>(null);
  useEffect(() => {
    if (aiSummary.status !== 'ready' || summaryCallId === null || summaryApplied.current === summaryCallId)
      return;
    summaryApplied.current = summaryCallId;
    setDraft(applySummary(draft, aiSummary.summary, bookedHere));
  }, [aiSummary, summaryCallId, draft, setDraft, bookedHere]);
  const aiHint =
    aiSummary.status === 'loading'
      ? SUMMARY_LOADING_TEXT
      : aiSummary.status === 'ready'
        ? SUMMARY_SUGGESTION_TEXT
        : null;

  const onGlowDone = useCallback(() => setGlow(null), []);

  // --- actions ----------------------------------------------------------------------------------------
  function dial() {
    if (!current || !session) return;
    call.reset();
    autoDialed.current = current.entryId;
    void call.start({ contactId: current.contact.id, source: 'dialer', sessionId: session.id });
  }

  async function book(eventType: EventTypeRef, startsAt: string, advisorId: string) {
    if (!session || !current) return;
    setBooking(startsAt);
    setBookingError(null);
    try {
      const result = await bookFromSession(session.id, { eventTypeId: eventType.id, startsAt, advisorId });
      setBooked((all) => ({ ...all, [current.entryId]: result }));
      setDrafts((all) => ({
        ...all,
        [current.entryId]: { ...(all[current.entryId] ?? draft), disposition: 'appointment_set' },
      }));
      queryClient.setQueryData(dialerKeys.metrics(), result.metrics);
      setGlow(result);
      void queryClient.invalidateQueries({ queryKey: dialerKeys.current() });
    } catch (caught) {
      setBookingError(errorMessage(caught));
    } finally {
      setBooking(null);
    }
  }

  async function pause() {
    if (!session) return;
    setPending('pause');
    setBanner(null);
    setCountdown(null);
    try {
      setState(await sessionAction(session.id, 'pause'));
      setSaved(null);
    } catch (caught) {
      setBanner(errorMessage(caught));
    } finally {
      setPending(null);
    }
  }

  async function resume() {
    if (!session) return;
    setPending('resume');
    setBanner(null);
    try {
      setState(await sessionAction(session.id, 'resume'));
    } catch (caught) {
      setBanner(errorMessage(caught));
    } finally {
      setPending(null);
    }
  }

  async function end() {
    setPending('end');
    setBanner(null);
    setCountdown(null);
    try {
      await endSession(sessionId);
      call.reset();
      await queryClient.invalidateQueries({ queryKey: ['dialer'] });
      void navigate('/dialer');
    } catch (caught) {
      setBanner(errorMessage(caught));
    } finally {
      setPending(null);
    }
  }

  async function save() {
    if (!session || !current || draft.disposition === null) return;
    const body: DialerDispositionBody = { disposition: draft.disposition };
    const note = draft.note.trim();
    if (note) body.note = note;
    if (draft.disposition === 'no_interest' || draft.disposition === 'block') {
      if (!note) {
        setFieldErrors({ reason: 'Bitte geben Sie einen Grund an.' });
        return;
      }
      body.reason = note;
    }
    if (draft.disposition === 'follow_up') {
      if (!draft.followUpOn) {
        setFieldErrors({ followUp: 'Bitte geben Sie eine Wiedervorlage an.' });
        return;
      }
      body.followUp = draft.followUpTime
        ? { dueOn: draft.followUpOn, dueTime: draft.followUpTime }
        : { dueOn: draft.followUpOn };
    }
    setFieldErrors({});
    setPending('save');
    setBanner(null);
    try {
      const response = await saveDisposition(session.id, body);
      setSaved({
        entryId: current.entryId,
        name: current.contact.name,
        durationSeconds: localSeconds ?? serverDuration,
        booked: bookedHere,
        recordingSaved: current.call !== null && current.call.recording.status !== 'none',
        draft,
        suggestion:
          response.suggestion === 'no_interest'
            ? 'Die Versuche sind ausgeschöpft — Vorschlag beim nächsten Mal: Kein Interesse.'
            : null,
      });
      setState(response.state);
      call.reset();
      if (response.autoDialInSeconds !== null) setCountdown(response.autoDialInSeconds);
      else {
        setPending(null);
        await next();
        return;
      }
    } catch (caught) {
      if (caught instanceof ApiError && Object.keys(caught.fields).length > 0) {
        setFieldErrors({
          reason: caught.fields['reason'],
          followUp: caught.fields['followUp'] ?? caught.fields['followUp.dueOn'],
        });
      } else setBanner(errorMessage(caught));
    }
    setPending(null);
  }

  // --- render -----------------------------------------------------------------------------------------
  const crumbText =
    view === 'pause'
      ? 'Dialer · Pause'
      : view === 'disposition' || view === 'saved'
        ? 'Dialer · Gespräch beendet'
        : view === 'call'
          ? 'Dialer · Gespräch läuft'
          : 'Dialer';
  const callError =
    call.state.error !== null && (call.state.source === 'dialer' || call.state.phase === 'idle')
      ? call.state.error
      : null;

  if (dialer.isError) {
    return (
      <DialerPage crumb={<Crumb>Dialer</Crumb>}>
        <LoadError
          error={dialer.error}
          what="Die Queue"
          retrying={dialer.isFetching}
          onRetry={() => void dialer.refetch()}
        />
      </DialerPage>
    );
  }

  if (view === 'loading' || !state) {
    return (
      <DialerPage crumb={<Crumb>Dialer</Crumb>}>
        <Skeleton width="100%" height={70} />
        <MetricsBar metrics={undefined} loading />
        <Skeleton width="100%" height={420} />
      </DialerPage>
    );
  }

  if (view === 'other') return <EndedSession sessionId={sessionId} runningId={session?.id ?? null} />;

  let barPhase: BarPhase = 'idle';
  if (view === 'call' && liveHere) {
    barPhase = bookedHere ? 'booked' : call.state.phase === 'connected' ? 'connected' : 'dialing';
  } else if (view === 'disposition' || view === 'saved') barPhase = 'ended';
  const barSeconds =
    view === 'call'
      ? localSeconds
      : view === 'saved'
        ? (saved?.durationSeconds ?? null)
        : (localSeconds ?? serverDuration);
  const showBar = view === 'call' || view === 'disposition' || view === 'saved';
  const live: LiveRow | null =
    liveHere && (call.state.phase === 'dialing' || call.state.phase === 'connected')
      ? {
          status: liveStatus(call.state.phase, localSeconds),
          text: [
            session?.dialModeLabel,
            call.state.view?.poolLabel ? `Rufnummern-Pool ${call.state.view.poolLabel}` : null,
            telephony.data?.recording ? 'Aufnahme läuft' : null,
          ]
            .filter(Boolean)
            .join(' · '),
        }
      : null;

  return (
    <div className={styles.stage}>
      <DialerPage crumb={<Crumb>{crumbText}</Crumb>}>
        {banner ? <Banner tone="danger">{banner}</Banner> : null}
        {callError && view !== 'pause' ? (
          <Banner tone="danger" role="alert">
            {callError}
          </Banner>
        ) : null}
        {showBar ? (
          <DialerQueueBar
            state={state}
            call={call.state}
            phase={barPhase}
            seconds={barSeconds}
            canDial={view === 'call' && !liveHere}
            pausing={pending === 'pause'}
            onDial={dial}
            onMute={() => call.mute(!call.state.muted)}
            onPause={() => void pause()}
            onHangup={call.hangup}
          />
        ) : null}
        <MetricsBar metrics={metrics.data} loading={metrics.isPending} />

        {view === 'pause' ? (
          <PausedSession
            sessionId={sessionId}
            busy={pending === 'resume' ? 'resume' : pending === 'end' ? 'end' : null}
            onResume={() => void resume()}
            onEnd={() => void end()}
          />
        ) : null}

        {view === 'exhausted' ? (
          <section className={styles.pauseCard} aria-labelledby="dialer-exhausted-title">
            <h2 id="dialer-exhausted-title" className={styles.h2}>
              Gerade kein Kontakt wählbar
            </h2>
            <p className={styles.lead}>In Ihren Listen ist gerade kein weiterer Kontakt wählbar.</p>
            <div className={styles.pauseCtas}>
              <button
                type="button"
                className={styles.cta}
                disabled={pending !== null}
                aria-busy={pending === 'end' || undefined}
                onClick={() => void end()}
              >
                Queue beenden
              </button>
              <button
                type="button"
                className={styles.ctaSecondary}
                disabled={pending !== null}
                onClick={() => void pause()}
              >
                Queue pausieren
              </button>
            </div>
          </section>
        ) : null}

        {view === 'between' && state.nextUp ? (
          <section className={styles.pauseCard} aria-labelledby="dialer-between-title">
            <h2 id="dialer-between-title" className={styles.h2}>
              Als nächstes: {state.nextUp.name}
            </h2>
            <p className={styles.lead}>
              {[state.nextUp.listName, state.nextUp.lifecycle.label].filter(Boolean).join(' · ')}
            </p>
            <div className={styles.pauseCtas}>
              <button
                type="button"
                className={styles.cta}
                disabled={pending !== null}
                aria-busy={pending === 'next' || undefined}
                onClick={() => void next()}
              >
                Weiter wählen
              </button>
              <button
                type="button"
                className={styles.ctaSecondary}
                disabled={pending !== null}
                onClick={() => void pause()}
              >
                Queue pausieren
              </button>
            </div>
          </section>
        ) : null}

        {view === 'call' && current ? (
          <div className={styles.callGrid}>
            <ContactColumn current={current} />
            <TimelineCard rows={current.timeline} live={live} />
            <div className={styles.column}>
              {current.contact.briefing ? <BriefingCard briefing={current.contact.briefing} /> : null}
              {liveHere && telephony.data?.transcription ? (
                <LiveTranscriptPanel
                  callId={call.state.callId}
                  contactName={current.contact.name}
                  onBook={focusBooking}
                />
              ) : null}
              <BookingCard
                key={current.entryId}
                current={current}
                chosen={chosenTypes[current.entryId] ?? null}
                onChoose={(value) => setChosenTypes((all) => ({ ...all, [current.entryId]: value }))}
                booked={booked[current.entryId] ?? null}
                booking={booking}
                error={bookingError}
                canBook={current.call !== null || liveHere}
                onBook={(eventType, startsAt, advisorId) => void book(eventType, startsAt, advisorId)}
              />
              <NoteCard
                note={notes[current.entryId] ?? ''}
                onNote={(value) => {
                  setNotes((all) => ({ ...all, [current.entryId]: value }));
                  setDrafts((all) => {
                    const existing = all[current.entryId];
                    return existing ? { ...all, [current.entryId]: { ...existing, note: value } } : all;
                  });
                }}
                recording={liveHere && (telephony.data?.recording ?? false)}
              />
            </div>
          </div>
        ) : null}

        {view === 'disposition' && current ? (
          <DispositionView
            name={current.contact.name}
            durationSeconds={localSeconds ?? serverDuration}
            recordingSaved={current.call !== null && current.call.recording.status !== 'none'}
            booked={bookedHere}
            draft={draft}
            onDraft={setDraft}
            errors={fieldErrors}
            banner={null}
            saving={pending === 'save'}
            saved={false}
            countdown={null}
            dialMode={session?.dialMode ?? 'power'}
            nextUp={state.nextUp}
            suggestion={null}
            aiHint={aiHint}
            onSave={() => void save()}
            onPause={() => void pause()}
            onCancelCountdown={() => setCountdown(null)}
            onNext={() => void next()}
          />
        ) : null}

        {view === 'saved' ? (
          <DispositionView
            name={saved?.name ?? ''}
            durationSeconds={saved?.durationSeconds ?? null}
            recordingSaved={saved?.recordingSaved ?? false}
            booked={saved?.booked ?? false}
            draft={saved?.draft ?? EMPTY_DRAFT}
            onDraft={() => undefined}
            errors={{}}
            banner={null}
            saving={pending === 'next'}
            saved
            countdown={countdown}
            dialMode={session?.dialMode ?? 'power'}
            nextUp={state.nextUp}
            suggestion={saved?.suggestion ?? null}
            onSave={() => undefined}
            onPause={() => void pause()}
            onCancelCountdown={() => setCountdown(null)}
            onNext={() => void next()}
          />
        ) : null}
      </DialerPage>
      {glow ? <GlowOverlay glow={glow} onDone={onGlowDone} /> : null}
    </div>
  );
}

function PausedSession({
  sessionId,
  busy,
  onResume,
  onEnd,
}: {
  sessionId: string;
  busy: 'resume' | 'end' | null;
  onResume: () => void;
  onEnd: () => void;
}) {
  const navigate = useNavigate();
  const evaluation = useEvaluation(sessionId);
  return (
    <PauseView
      evaluation={evaluation.data}
      error={evaluation.isError ? evaluation.error : null}
      retrying={evaluation.isFetching}
      onRetry={() => void evaluation.refetch()}
      busy={busy}
      onResume={onResume}
      onEnd={onEnd}
      onBack={() => void navigate('/dialer')}
    />
  );
}

/** A session that is not the running one (ended, or another queue runs): its evaluation or the way back. */
function EndedSession({ sessionId, runningId }: { sessionId: string; runningId: string | null }) {
  const navigate = useNavigate();
  const evaluation = useEvaluation(sessionId);
  const metrics = useTodayMetrics();
  return (
    <DialerPage crumb={<Crumb>Dialer · Auswertung</Crumb>}>
      {runningId !== null ? <Banner tone="accent">Sie haben bereits eine laufende Queue.</Banner> : null}
      <MetricsBar metrics={metrics.data} loading={metrics.isPending} />
      <PauseView
        evaluation={evaluation.data}
        error={evaluation.isError ? evaluation.error : null}
        retrying={evaluation.isFetching}
        onRetry={() => void evaluation.refetch()}
        busy={null}
        onResume={() => void navigate(runningId ? `/dialer/session/${runningId}` : '/dialer')}
        onEnd={() => void navigate('/dialer')}
        onBack={() => void navigate(runningId ? `/dialer/session/${runningId}` : '/dialer')}
      />
    </DialerPage>
  );
}
